<template>
  <v-container fluid pt-0>
    <v-row align="center" class="pb-2">
      <h1 class="headline">Sage PI Approvals</h1>

      <v-spacer></v-spacer>
      <v-col class="py-0">
        <list-filters
          :filters="filters"
          label="Find Approval"
          ref-name="purchaseInvoiceApprovalList"
          @searchParamsChanged="searchParamsChanged"
          :expansionPanel="false"
        ></list-filters>
      </v-col>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" fab dark color="primary" @click="onCreateNew">
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Create New PI Approval</span>
      </v-tooltip>
    </v-row>
    <v-row>
      <v-col class="pa-0">
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="$apollo.loading"
          :search="search"
          item-key="id"
          class="elevation-1"
          v-bind="pagingOptions"
          mobile-break-point="1264"
          :height="gridHeight"
          hide-default-footer
          @update:options="onListOptionsUpdate"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          fixed-header
          @current-items="dataTableReady"
        >
          <template v-slot:item.createdAt="{ item }">{{
            item.createdAt | formatDate
          }}</template>
          <template v-slot:item.approvedAmount="{ item }">
            {{ item.approvedAmount | priceLabeler }}
          </template>
          <template v-slot:item.approvedNetAmount="{ item }">
            {{ item.approvedNetAmount | priceLabeler }}
          </template>
          <template v-slot:item.approvedVatAmount="{ item }">
            {{ item.approvedVatAmount | priceLabeler }}
          </template>
          <template v-slot:item.actions="{ item }">
            <default-list-actions
              :item="item"
              hideView
              :hideRemove="item.hasPayments"
              @editItem="onEditItem"
              @deleteItem="onDeleteItem"
            ></default-list-actions>
          </template>
          <template v-slot:item.exported="{ item }">
            <v-simple-checkbox
              v-model="item.exported"
              :disabled="true"
            ></v-simple-checkbox>
          </template>
        </v-data-table>
        <get-more-results
          :first="first"
          @limitChange="onLimitChange"
          :items="piApprovals"
          :totalItems="totalItems"
          :loading="$apollo.loading"
          :loadMoreEnabled="loadMoreEnabled"
          @loadMore="loadMore('piApprovals')"
        ></get-more-results>
      </v-col>
    </v-row>
    <purchase-invoice-approval-dialog
      :show="showDialog"
      @close="showDialog = false"
      v-model="editedItem"
      :key="dialogKey"
      @refresh="onRefreshList"
      @remove="onDeleteItem"
    ></purchase-invoice-approval-dialog>
    <v-row v-if="debug">
      <code>{{ sort }}</code>
    </v-row>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import { ListMixin } from "../../../../mixins/ListMixin";
import ListFilters from "../../shared/list-filters/ListFilters";
import GetMoreResults from "../../shared/GetMoreResults";
import PurchaseInvoiceApprovalDialog from "./PurchaseInvoiceApprovalDialog.vue";
import { removeGQLTypename } from "../../../../helpers";
import DefaultListActions from "../../shared/DefaultListActions.vue";
import purchaseInvoiceApprovalsFilters from "./pi-approvals-filters";
const randomize = require("randomatic");
const PI_APPROVALS_LIST = gql`
  query piApprovals(
    $after: String
    $filter: PurchaseInvoiceApprovalFilterType
    $sort: SortOrderType
    $first: Int
  ) {
    piApprovals(first: $first, after: $after, filter: $filter, orderBy: $sort) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          approvalId
          name
          invoiceDate
          dueDate
          reference
          netAmount
          vatAmount
          totalAmount
          approvedNetAmount
          approvedVatAmount
          approvedAmount
          supplierName
          supplierId
          suppliersNames
          currency
          createdAt
          createdBy
          notes
          exported
          headOfficeId
          headOfficeName
          hasPayments
        }
      }
    }
  }
`;
const REMOVE_APPROVAL = gql`
  mutation removePiApproval($id: ID!) {
    deletePiApprovals(id: $id) {
      id
    }
  }
`;
export default {
  components: {
    PurchaseInvoiceApprovalDialog,
    DefaultListActions,
    ListFilters,
    GetMoreResults,
  },

  props: {
    refName: {
      type: String,
      default: () => "pi-approvals",
    },
  },

  name: "purchase-invoice-approval-list",
  mixins: [ListMixin],
  data() {
    return {
      debug: false,
      dialogKey: randomize("Aa0", 32),
      purchaseInvoiceApprovalsFilters: purchaseInvoiceApprovalsFilters,
      headers: [
        {
          text: "ID",
          value: "approvalId",
          name: "approvalId",
          sortable: true,
        },
        {
          text: "Created At",
          value: "createdAt",
          name: "createdAt",
          sortable: true,
        },
        {
          text: "Supplier(s)",
          value: "suppliersNames",
          name: "suppliersNames",
          sortable: true,
        },
        {
          text: "HeadOffice",
          value: "headOfficeName",
          name: "headOfficeName",
          sortable: true,
        },
        {
          text: "Supplier Invoice Number",
          value: "name",
          name: "name",
          sortable: true,
        },
        {
          text: "Net Amount",
          value: "approvedNetAmount",
          name: "approvedNetAmount",
          sortable: true,
        },
        {
          text: "Vat Amount",
          value: "approvedVatAmount",
          name: "approvedVatAmount",
          sortable: true,
        },
        {
          text: "Amount",
          value: "approvedAmount",
          name: "approvedAmount",
          sortable: true,
        },
        {
          text: "Posted",
          value: "exported",
          name: "exported",
          sortable: true,
        },
        {
          text: "Created by",
          value: "createdBy",
          name: "createdBy",
          sortable: true,
        },

        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
      showDialog: false,
      editedItem: null,
    };
  },

  computed: {
    items() {
      return this.piApprovals && this.piApprovals.edges
        ? this.piApprovals.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return purchaseInvoiceApprovalsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    onDeleteItem(item) {
      this.$apollo
        .mutate({
          mutation: REMOVE_APPROVAL,
          variables: {
            id: item.id,
          },
          refetchQueries: [
            {
              query: PI_APPROVALS_LIST,
              variables: {
                first: this.first,
                after: this.after,
                filter: this.searchParams,
                sort: this.sort,
              },
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "PI Approvals Removed.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },

    onCreateNew() {
      this.editedItem = null;
      this.dialogKey = randomize("Aa0", 32);
      this.setBalanceFilterForPIs();
      this.showDialog = true;
    },
    onEditItem(payload) {
      this.editedItem = { ...payload };
      this.setBalanceFilterForPIs();
      this.showDialog = true;
    },
    onRefreshList() {
      if (this.$apollo.queries.piApprovals != null) {
        this.$apollo.queries.piApprovals.refetch();
      }
    },
    setBalanceFilterForPIs() {
      //set default balance filter #7055
      const balanceNotEqZero = {
        id: "Oqcm11ouWAlO7yF2P3yfFPmGH9NMJYS5",
        filterName: "balance",
        filter: { balance_not: 0 },
        label: "Balance Amount !=",
        value: 0,
      };
      const cachedFilters = this.$store.getters.localSettings(
        "piApproveInvoicesFilters",
        []
      );
      this.$store.dispatch("updateLocalSettings", {
        piApproveInvoicesFilters: [
          ...cachedFilters.filter((f) => f.filterName != "balance"),
          balanceNotEqZero,
        ],
      });
    },
  },

  apollo: {
    piApprovals: {
      query: PI_APPROVALS_LIST,
      variables() {
        return {
          first: this.first,
          after: this.after,
          filter: this.searchParams,
          sort: this.sort,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.piApprovals.totalCount;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/finances/pi-approvals/PurchasInvoiceApprovalList.vue